import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle, RefreshCw, Search, X, Loader2,
  ShieldAlert, ShieldCheck, AlertCircle, Clock, ChevronDown,
  ExternalLink, Package, XCircle, CheckCircle2, Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';

const SEVERITY_CONFIG = {
  CRITICAL: { label: 'Critical', icon: XCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  WARNING: { label: 'Warning', icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  INFO: { label: 'Info', icon: Info, color: 'text-[#C45500]', bg: 'bg-[#FFF4E6]', border: 'border-[#FFD89E]', dot: 'bg-[#232F3E]' },
};

const TYPE_CONFIG = {
  SUPPRESSED: { label: 'Suppressed', icon: ShieldAlert, color: 'text-red-600' },
  DEFECT: { label: 'Defect', icon: AlertCircle, color: 'text-amber-600' },
  INACTIVE: { label: 'Inactive', icon: Package, color: 'text-slate-500' },
  STRANDED: { label: 'Stranded', icon: Package, color: 'text-orange-600' },
  HIGH_RETURNS: { label: 'High Returns', icon: XCircle, color: 'text-rose-600' },
};

const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  RESOLVED: { label: 'Resolved', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
};

export default function PolicyWarningsPage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(() =>
    (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchWarnings = async () => {
    if (!sellerId) return;
    setLoading(true);
    try {
      const params = { sellerId };
      if (selectedAccountId) params.sellerAccountId = selectedAccountId;
      const res = await apiClient.get(ENDPOINTS.POLICY_WARNINGS.GET_ALL, { params });
      setWarnings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load policy warnings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarnings();
  }, [sellerId, selectedAccountId]);

  const handleSync = async () => {
    const accountId = selectedAccountId || sellerChannels[0]?.id;
    if (!accountId) {
      toast.error('No marketplace connected for this seller');
      return;
    }
    setSyncing(true);
    try {
      await apiClient.post(ENDPOINTS.POLICY_WARNINGS.SYNC, null, { params: { sellerAccountId: accountId } });
      toast.success('Policy check completed');
      fetchWarnings();
    } catch (err) {
      toast.error('Policy check failed: ' + (err?.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  // Filter
  const filtered = useMemo(() => {
    return warnings.filter(w => {
      if (severityFilter && w.severity !== severityFilter) return false;
      if (statusFilter && w.status !== statusFilter) return false;
      if (typeFilter && w.warningType !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (w.asin || '').toLowerCase().includes(q) ||
          (w.sku || '').toLowerCase().includes(q) ||
          (w.productTitle || '').toLowerCase().includes(q) ||
          (w.reason || '').toLowerCase().includes(q) ||
          (w.sellerName || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [warnings, severityFilter, statusFilter, typeFilter, search]);

  // Stats (from unfiltered)
  const stats = useMemo(() => {
    const s = { total: warnings.length, active: 0, resolved: 0, critical: 0, warning: 0, info: 0, suppressed: 0, defect: 0, inactive: 0, stranded: 0, highReturns: 0 };
    warnings.forEach(w => {
      if (w.status === 'ACTIVE') s.active++;
      if (w.status === 'RESOLVED') s.resolved++;
      if (w.severity === 'CRITICAL') s.critical++;
      if (w.severity === 'WARNING') s.warning++;
      if (w.severity === 'INFO') s.info++;
      if (w.warningType === 'SUPPRESSED') s.suppressed++;
      if (w.warningType === 'DEFECT') s.defect++;
      if (w.warningType === 'INACTIVE') s.inactive++;
      if (w.warningType === 'STRANDED') s.stranded++;
      if (w.warningType === 'HIGH_RETURNS') s.highReturns++;
    });
    return s;
  }, [warnings]);

  const hasFilters = severityFilter || statusFilter || typeFilter || search;

  const selectCls = "px-3 py-[7px] border border-slate-200 rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all";

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 30) return `${diffD}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Policy Warnings</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Suppressed listings, quality defects & compliance issues
            </p>
          </div>
          <div className="flex items-center gap-2">
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />
            <button onClick={fetchWarnings} disabled={loading}
              className="p-2.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50" title="Refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleSync}
              disabled={syncing || sellerChannels.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#232F3E] text-white rounded-xl text-[13px] font-semibold hover:bg-[#37475A] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Checking...' : 'Check Now'}
            </button>
          </div>
        </div>

        {/* Stat Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: 'All', count: stats.total, key: '', active: !statusFilter },
            { label: 'Active', count: stats.active, key: 'ACTIVE', active: statusFilter === 'ACTIVE', dot: 'bg-red-500' },
            { label: 'Resolved', count: stats.resolved, key: 'RESOLVED', active: statusFilter === 'RESOLVED', dot: 'bg-emerald-500' },
          ].map(b => (
            <button
              key={b.label}
              onClick={() => setStatusFilter(b.active && b.key ? '' : b.key)}
              className={`inline-flex items-center gap-2 h-8 px-3 rounded-lg text-[12px] font-semibold border transition-all ${
                b.active ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {b.dot && <span className={`w-2 h-2 rounded-full ${b.active ? 'bg-white/80' : b.dot}`} />}
              {b.label}
              <span className={`text-[11px] ${b.active ? 'text-white/60' : 'text-slate-400'}`}>{b.count}</span>
            </button>
          ))}

          <div className="w-px h-5 bg-slate-200 mx-1" />

          {/* Severity badges */}
          {[
            { label: 'Critical', count: stats.critical, key: 'CRITICAL', dot: 'bg-red-500' },
            { label: 'Warning', count: stats.warning, key: 'WARNING', dot: 'bg-amber-500' },
            { label: 'Info', count: stats.info, key: 'INFO', dot: 'bg-[#232F3E]' },
          ].map(b => (
            <button
              key={b.label}
              onClick={() => setSeverityFilter(severityFilter === b.key ? '' : b.key)}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold border transition-all ${
                severityFilter === b.key ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${severityFilter === b.key ? 'bg-white/80' : b.dot}`} />
              {b.label}
              <span className={`text-[11px] ${severityFilter === b.key ? 'text-white/60' : 'text-slate-400'}`}>{b.count}</span>
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200/80 px-4 py-3 flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ASIN, SKU, product, reason..."
              className="w-full pl-9 pr-8 py-[7px] border border-slate-200 rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] transition-all placeholder:text-slate-400"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <X size={13} className="text-slate-400" />
              </button>
            )}
          </div>

          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={selectCls}>
            <option value="">All Types</option>
            <option value="SUPPRESSED">Suppressed</option>
            <option value="DEFECT">Defect</option>
            <option value="INACTIVE">Inactive</option>
            <option value="STRANDED">Stranded Inventory</option>
            <option value="HIGH_RETURNS">High Returns</option>
          </select>

          {hasFilters && (
            <button onClick={() => { setSearch(''); setSeverityFilter(''); setStatusFilter(''); setTypeFilter(''); }} className="text-[12px] text-red-500 font-medium flex items-center gap-1">
              <X size={13} /> Clear
            </button>
          )}

          <span className="ml-auto text-[12px] text-slate-400 tabular-nums">{filtered.length} of {warnings.length}</span>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-[#f08804] animate-spin" />
          </div>
        ) : warnings.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200/80 px-6 py-20 text-center">
            <ShieldCheck size={48} className="mx-auto text-emerald-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No Policy Warnings</h3>
            <p className="text-sm text-slate-400 mt-1.5 max-w-md mx-auto">
              {sellerChannels.length > 0
                ? 'Click "Check Now" to scan for suppressed listings, quality defects, and compliance issues.'
                : 'No marketplace connected for this seller.'}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200/80 px-6 py-16 text-center">
            <Search size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No warnings match your filters.</p>
          </div>
        ) : (
          /* Warning Table */
          <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-5">Severity</th>
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Type</th>
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">ASIN / SKU</th>
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Product</th>
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Reason</th>
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
                  <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Detected</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(w => {
                  const sev = SEVERITY_CONFIG[w.severity] || SEVERITY_CONFIG.WARNING;
                  const type = TYPE_CONFIG[w.warningType] || TYPE_CONFIG.DEFECT;
                  const st = STATUS_CONFIG[w.status] || STATUS_CONFIG.ACTIVE;
                  const SevIcon = sev.icon;
                  const TypeIcon = type.icon;

                  return (
                    <tr
                      key={w.id}
                      className="border-b border-slate-100/80 last:border-0 hover:bg-slate-50/60 cursor-pointer transition-colors"
                      onClick={() => setExpandedId(expandedId === w.id ? null : w.id)}
                    >
                      <td className="py-3 px-5">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${sev.bg} ${sev.color} border ${sev.border}`}>
                          <SevIcon size={11} />
                          {sev.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 text-[12px] font-medium ${type.color}`}>
                          <TypeIcon size={13} />
                          {type.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          {w.asin && <p className="text-sm font-semibold text-slate-900 font-mono">{w.asin}</p>}
                          {w.sku && <p className="text-[10px] text-slate-400 font-mono">{w.sku}</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-[12px] text-slate-700 truncate max-w-[200px]">{w.productTitle || '—'}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-[12px] text-slate-500 truncate max-w-[250px]">{w.reason || '—'}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.bg} ${st.text} border ${st.border}`}>
                          {w.status === 'RESOLVED' ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
                          {st.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-[11px] text-slate-400" title={w.detectedAt}>{timeAgo(w.detectedAt)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
